import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';
import {
	FileText,
	Download,
	TrendingUp,
	Calculator,
	Calendar,
	AlertCircle,
	CheckCircle,
	Loader,
	ChevronDown,
	ChevronRight,
} from 'lucide-react';

import LineChart from '../components/charts/LineChart';
import BarChart from '../components/charts/BarChart';
import { useFinanceStore, Transaction } from '../store/useFinanceStore';
import { useTheme } from '../contexts/ThemeContext';
import { useN8NPdf } from '../hooks/useN8NPdf';
import { calculateMonthlyTotals, isCountableTransaction, normalizeStatus } from '../utils/transactionMetrics';

const toDateInputValue = (date: Date) => format(date, 'yyyy-MM-dd');
const capitalize = (value: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);
const formatCurrency = (value: number) => `R$ ${value.toLocaleString('pt-BR')}`;

interface MonthlyDataItem {
	month: string;
	income: number;
	expenses: number;
	balance: number;
}

const Relatorios: React.FC = () => {
	const { theme } = useTheme();
	const { transactions, loadTransactions, error: storeError } = useFinanceStore();
	const { generatePdf, isLoading: pdfLoading, error: pdfError, success, clearMessages } = useN8NPdf();

	const today = useMemo(() => new Date(), []);
        const [selectedCategory, setSelectedCategory] = useState<string>('all');
        const [dateRange, setDateRange] = useState(() => {
                const start = new Date(today.getFullYear(), today.getMonth(), 1);
                const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                return {
                        start: toDateInputValue(start),
                        end: toDateInputValue(end),
                };
        });
	const [expandedMonthIndex, setExpandedMonthIndex] = useState<number | null>(null);
	const [isDetailsExpanded, setIsDetailsExpanded] = useState<boolean>(false);

	useEffect(() => {
		if (!transactions.length) {
			loadTransactions().catch(() => {
				// erros tratados pelo store
			});
		}
	}, [transactions.length, loadTransactions]);

	useEffect(() => {
		clearMessages();
	}, [dateRange, selectedCategory, clearMessages]);

		const categories = useMemo(() => {
			const unique = new Set<string>();
			transactions.forEach((transaction) => unique.add(transaction.category));
			return Array.from(unique).sort((a, b) => a.localeCompare(b, 'pt-BR'));
		}, [transactions]);

                const filteredTransactions = useMemo(() => {
                        const start = dateRange.start ? new Date(dateRange.start) : null;
                        const end = dateRange.end ? new Date(dateRange.end) : null;
                        if (end) {
                                end.setHours(23, 59, 59, 999);
                        }

			const result = transactions.filter((transaction) => {
				const txDate = new Date(transaction.date);
				if (Number.isNaN(txDate.getTime())) {
					return false;
				}

				if (selectedCategory !== 'all' && transaction.category !== selectedCategory) {
					return false;
				}

				if (start && txDate < start) {
					return false;
				}

				if (end && txDate > end) {
					return false;
				}

				return true;
			});

                        return result.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
                }, [transactions, dateRange, selectedCategory]);

                const referenceYear = useMemo(() => {
                        const base = dateRange.end ? new Date(dateRange.end) : today;
                        return Number.isNaN(base.getTime()) ? today.getFullYear() : base.getFullYear();
                }, [dateRange.end, today]);

                const yearlyTotals = useMemo(() => {
                        return transactions.reduce(
                                (acc, transaction) => {
                                        const txDate = new Date(transaction.date);
                                        if (Number.isNaN(txDate.getTime()) || txDate.getFullYear() !== referenceYear) {
                                                return acc;
                                        }

                                        if (selectedCategory !== 'all' && transaction.category !== selectedCategory) {
                                                return acc;
                                        }

                                        if (!isCountableTransaction(transaction, today)) {
                                                return acc;
                                        }

                                        if (transaction.amount >= 0) {
                                                acc.income += transaction.amount;
                                        } else {
                                                acc.expenses += Math.abs(transaction.amount);
                                        }

					acc.balance = acc.income - acc.expenses;
					return acc;
                                },
                                { income: 0, expenses: 0, balance: 0 }
                        );
                }, [referenceYear, selectedCategory, today, transactions]);

		const { monthlyData, transactionsByMonth } = useMemo(() => {
			const bucketMap = new Map<
				string,
				{
					label: string;
					monthIndex: number;
					year: number;
					income: number;
					expenses: number;
					balance: number;
					transactions: Transaction[];
				}
			>();

                        filteredTransactions.forEach((transaction) => {
                                const txDate = new Date(transaction.date);
                                if (Number.isNaN(txDate.getTime())) {
                                        return;
                                }

                                if (!isCountableTransaction(transaction, today)) {
                                        return;
                                }

                                const key = `${txDate.getFullYear()}-${txDate.getMonth()}`;
                                let bucket = bucketMap.get(key);
				if (!bucket) {
					bucket = {
						label: format(txDate, 'MMMM yyyy', { locale: ptBR }),
						monthIndex: txDate.getMonth(),
						year: txDate.getFullYear(),
						income: 0,
						expenses: 0,
						balance: 0,
						transactions: [],
					};
					bucketMap.set(key, bucket);
				}

				if (transaction.amount >= 0) {
					bucket.income += transaction.amount;
				} else {
					bucket.expenses += Math.abs(transaction.amount);
				}
				bucket.balance = bucket.income - bucket.expenses;
				bucket.transactions.push(transaction);
			});

			const ordered = Array.from(bucketMap.values()).sort((a, b) => {
				if (a.year === b.year) {
					return b.monthIndex - a.monthIndex;
				}
				return b.year - a.year;
			});

			return {
				monthlyData: ordered.map(({ label, income, expenses, balance }) => ({
					month: label,
					income,
					expenses,
					balance,
				})),
				transactionsByMonth: ordered.map(({ transactions }) =>
					transactions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
				),
			};
                }, [filteredTransactions, today]);

                const currentMonthData = useMemo(() => {
                        const source =
                                selectedCategory === 'all'
                                        ? transactions
                                        : transactions.filter((transaction) => transaction.category === selectedCategory);

                        return calculateMonthlyTotals(source, today);
                }, [selectedCategory, today, transactions]);

                const totals = useMemo(() => {
                        return filteredTransactions.reduce(
                                (acc, transaction) => {
                                        if (!isCountableTransaction(transaction, today)) {
                                                return acc;
                                        }

                                        if (transaction.amount >= 0) {
                                                acc.income += transaction.amount;
                                        } else {
                                                acc.expenses += Math.abs(transaction.amount);
                                        }
                                        return acc;
                                },
                                { income: 0, expenses: 0 }
                        );
                }, [filteredTransactions, today]);

		const totalIncome = totals.income;
		const totalExpenses = totals.expenses;
		const savingsRateValue = totalIncome > 0 ? ((totalIncome - totalExpenses) / totalIncome) * 100 : 0;
		const savingsRate = savingsRateValue.toFixed(1);
		const averageIncome = totalIncome / 4;

		const currentMonthLabel = useMemo(
			() => capitalize(format(today, 'MMMM yyyy', { locale: ptBR })),
			[today]
		);

		const exportToPDF = async () => {
			if (!filteredTransactions.length) {
				return;
			}

			const pdfMonthlyData: MonthlyDataItem[] = monthlyData.map((item) => ({
				month: capitalize(item.month),
				income: item.income,
				expenses: item.expenses,
				balance: item.balance,
			}));

			await generatePdf({
				transactions: filteredTransactions.map((transaction) => ({
					date: transaction.date,
					description: transaction.description,
					category: transaction.category,
					amount: transaction.amount,
				})),
				dateRange,
				totalIncome,
				totalExpenses,
				savingsRate: `${savingsRate}%`,
				monthlyData: pdfMonthlyData,
			});
		};

		const exportToCSV = () => {
			if (!filteredTransactions.length) {
				return;
			}

			const headers = ['Data', 'Descrição', 'Categoria', 'Valor'];
			const rows = filteredTransactions.map((transaction) => [
				format(new Date(transaction.date), 'dd/MM/yyyy'),
				`"${transaction.description.replace(/"/g, '""')}"`,
				transaction.category,
				transaction.amount.toString(),
			]);

			const csvContent = [headers.join(','), ...rows.map((columns) => columns.join(','))].join('\n');
			const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
			const url = URL.createObjectURL(blob);

			const anchor = document.createElement('a');
			anchor.href = url;
			anchor.download = `relatorios-${dateRange.start}-a-${dateRange.end}.csv`;
			document.body.appendChild(anchor);
			anchor.click();
			document.body.removeChild(anchor);
			URL.revokeObjectURL(url);
		};

		const error = pdfError ?? storeError;
		const isLoading = pdfLoading;

		return (
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.3 }}
				className="space-y-6"
			>
				<div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
					<h2 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
						Relatórios
					</h2>
					<div className="flex flex-col gap-2">
						{error && (
							<motion.div
								initial={{ opacity: 0, y: -10 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: -10 }}
								className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
									theme === 'dark'
										? 'bg-red-900/20 text-red-400 border border-red-900/50'
										: 'bg-red-50 text-red-700 border border-red-200'
								}`}
							>
								<AlertCircle size={16} />
								<span className="text-sm">{error}</span>
							</motion.div>
						)}
						{success && (
							<motion.div
								initial={{ opacity: 0, y: -10 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: -10 }}
								className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
									theme === 'dark'
										? 'bg-green-900/20 text-green-400 border border-green-900/50'
										: 'bg-green-50 text-green-700 border border-green-200'
								}`}
							>
								<CheckCircle size={16} />
								<span className="text-sm">{success}</span>
							</motion.div>
						)}
						<div className="flex gap-2">
							<button
								onClick={exportToPDF}
								disabled={isLoading || !filteredTransactions.length}
								className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
									isLoading || !filteredTransactions.length
										? theme === 'dark'
											? 'bg-slate-600 text-gray-400 cursor-not-allowed'
											: 'bg-gray-300 text-gray-600 cursor-not-allowed'
										: theme === 'dark'
											? 'bg-slate-700 hover:bg-slate-600 text-white'
											: 'bg-gray-100 hover:bg-gray-200 text-gray-900'
								}`}
							>
								{isLoading ? <Loader size={16} className="animate-spin" /> : <Download size={16} />}
								{isLoading ? 'Gerando...' : 'PDF'}
							</button>
							<button
								onClick={exportToCSV}
								disabled={!filteredTransactions.length}
								className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
									!filteredTransactions.length
										? theme === 'dark'
											? 'bg-slate-600 text-gray-400 cursor-not-allowed'
											: 'bg-gray-300 text-gray-600 cursor-not-allowed'
										: theme === 'dark'
											? 'bg-slate-700 hover:bg-slate-600 text-white'
											: 'bg-gray-100 hover:bg-gray-200 text-gray-900'
								}`}
							>
								<Download size={16} />
								CSV
							</button>
						</div>
					</div>
				</div>

				<div className={`rounded-lg p-6 ${theme === 'dark' ? 'bg-slate-800' : 'bg-white border border-teal-200'}`}>
					<div className="flex items-center gap-3 mb-6">
						<Calendar className={theme === 'dark' ? 'text-cyan-400' : 'text-cyan-600'} size={24} />
						<h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
							Resumo Anual {referenceYear}
						</h3>
					</div>

					<div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
						<div className="text-center">
							<p className={`text-3xl font-bold mb-2 ${theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'}`}>
								{formatCurrency(yearlyTotals.income)}
							</p>
							<p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Total de Receitas</p>
						</div>
						<div className="text-center">
							<p className={`text-3xl font-bold mb-2 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
								{formatCurrency(yearlyTotals.expenses)}
							</p>
                                                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Total de Saídas</p>
						</div>
						<div className="text-center">
							<p className={`text-3xl font-bold mb-2 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
								{formatCurrency(yearlyTotals.balance)}
							</p>
							<p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Saldo Acumulado</p>
						</div>
					</div>
				</div>

				<div className={`rounded-lg p-6 ${theme === 'dark' ? 'bg-slate-800' : 'bg-white border border-teal-200'}`}>
					<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
						<div>
							<h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
								Resumo Mensal — {currentMonthLabel}
							</h3>
							<p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
								Visão rápida das entradas e saídas do período em foco.
							</p>
						</div>
						<div className={`text-xs font-medium px-3 py-1 rounded-full ${
							theme === 'dark' ? 'bg-slate-700 text-slate-200' : 'bg-teal-100 text-teal-700'
						}`}>
							Baseado no intervalo de datas selecionado
						</div>
					</div>

					<div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
						<div className={`rounded-lg p-4 border ${
							theme === 'dark' ? 'border-slate-700 bg-slate-900/60' : 'border-emerald-100 bg-emerald-50'
						}`}>
							<p className={`text-sm font-medium mb-2 ${theme === 'dark' ? 'text-emerald-300' : 'text-emerald-700'}`}>
								Receitas do mês
							</p>
							<p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
								{formatCurrency(currentMonthData.income)}
							</p>
						</div>
						<div className={`rounded-lg p-4 border ${
							theme === 'dark' ? 'border-slate-700 bg-slate-900/60' : 'border-rose-100 bg-rose-50'
						}`}>
                                                        <p className={`text-sm font-medium mb-2 ${theme === 'dark' ? 'text-rose-300' : 'text-rose-700'}`}>
                                                                Saídas do mês
							</p>
							<p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
								{formatCurrency(currentMonthData.expenses)}
							</p>
						</div>
						<div className={`rounded-lg p-4 border ${
							theme === 'dark' ? 'border-slate-700 bg-slate-900/60' : 'border-sky-100 bg-sky-50'
						}`}>
							<p className={`text-sm font-medium mb-2 ${theme === 'dark' ? 'text-sky-300' : 'text-sky-700'}`}>
								Saldo consolidado
							</p>
							<p className={`text-2xl font-bold ${
								currentMonthData.balance >= 0
									? theme === 'dark' ? 'text-emerald-300' : 'text-emerald-700'
									: theme === 'dark' ? 'text-rose-300' : 'text-rose-700'
							}`}>
								{formatCurrency(currentMonthData.balance)}
							</p>
						</div>
					</div>
				</div>

				<div className={`rounded-lg p-6 ${theme === 'dark' ? 'bg-slate-800' : 'bg-white border border-teal-200'}`}>
					<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
						<div>
							<h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
								Detalhamento mensal
							</h3>
							<p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
								💡 Clique em um mês para expandir o resumo e as transações.
							</p>
						</div>
					</div>

					<div className="space-y-2">
						{monthlyData.map((month, index) => {
							const isExpanded = expandedMonthIndex === index;
							const monthTransactions = transactionsByMonth[index] || [];

							return (
								<div
									key={`${month.month}-${index}`}
									className={`rounded-lg border ${
										theme === 'dark' ? 'border-slate-700 bg-slate-900/50' : 'border-gray-200 bg-gray-50'
									}`}
								>
									<button
										type="button"
										onClick={() => setExpandedMonthIndex(isExpanded ? null : index)}
										className="w-full px-4 py-3 flex items-center justify-between gap-3"
									>
										<div className="flex flex-col items-start">
											<span className={`text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
												{capitalize(month.month)}
											</span>
											<span className={`text-xs ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
                                                                                            Receitas: {formatCurrency(month.income)} · Saídas: {formatCurrency(month.expenses)}
											</span>
										</div>
										<div className="flex items-center gap-4">
											<span className={`text-sm font-semibold ${
												month.balance >= 0
													? theme === 'dark' ? 'text-emerald-300' : 'text-emerald-700'
													: theme === 'dark' ? 'text-rose-300' : 'text-rose-700'
											}`}>
												Saldo: {formatCurrency(month.balance)}
											</span>
											<ChevronDown
												size={18}
												className={`transition-transform ${isExpanded ? 'rotate-180' : ''} ${
													theme === 'dark' ? 'text-slate-300' : 'text-slate-500'
												}`}
											/>
										</div>
									</button>

									<AnimatePresence initial={false}>
										{isExpanded && (
											<motion.div
												initial={{ opacity: 0, height: 0 }}
												animate={{ opacity: 1, height: 'auto' }}
												exit={{ opacity: 0, height: 0 }}
												transition={{ duration: 0.25 }}
												className="px-4 pb-4"
											>
												<div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 pb-4 border-t border-dashed border-slate-600/40">
													<div>
														<span className={`block text-xs uppercase tracking-wide mb-1 ${
															theme === 'dark' ? 'text-slate-400' : 'text-slate-600'
														}`}>
															Receitas
														</span>
														<span className={`text-base font-semibold ${
															theme === 'dark' ? 'text-emerald-300' : 'text-emerald-700'
														}`}>
															{formatCurrency(month.income)}
														</span>
													</div>
													<div>
														<span className={`block text-xs uppercase tracking-wide mb-1 ${
															theme === 'dark' ? 'text-slate-400' : 'text-slate-600'
														}`}>
                                                                                                                    Saídas
														</span>
														<span className={`text-base font-semibold ${
															theme === 'dark' ? 'text-rose-300' : 'text-rose-700'
														}`}>
															{formatCurrency(month.expenses)}
														</span>
													</div>
													<div>
														<span className={`block text-xs uppercase tracking-wide mb-1 ${
															theme === 'dark' ? 'text-slate-400' : 'text-slate-600'
														}`}>
															Saldo do mês
														</span>
														<span className={`text-base font-semibold ${
															month.balance >= 0
																? theme === 'dark' ? 'text-emerald-300' : 'text-emerald-700'
																: theme === 'dark' ? 'text-rose-300' : 'text-rose-700'
														}`}>
															{formatCurrency(month.balance)}
														</span>
													</div>
												</div>

												<div className={`rounded-lg border ${
													theme === 'dark' ? 'border-slate-700 bg-slate-900/70' : 'border-gray-200 bg-white'
												}`}>
													<div className={`px-4 py-2 text-xs font-medium uppercase tracking-wide ${
														theme === 'dark' ? 'text-slate-400 border-b border-slate-700' : 'text-slate-600 border-b border-gray-200'
													}`}>
														Transações do mês ({monthTransactions.length})
													</div>
													<div className="divide-y divide-slate-700/40">
														{monthTransactions.length === 0 && (
															<p className={`px-4 py-3 text-sm ${
																theme === 'dark' ? 'text-slate-400' : 'text-slate-600'
															}`}>
																Nenhuma transação registrada para este mês.
															</p>
														)}
														{monthTransactions.slice(0, 5).map((transaction, transactionIndex) => {
															const amount = transaction.amount;
															const formattedValue = `${amount > 0 ? '+' : '-'}${formatCurrency(Math.abs(amount))}`;

															return (
																<div
																	key={`${transaction.id ?? transactionIndex}-${transaction.date}`}
																	className="px-4 py-3 flex items-center justify-between text-sm"
																>
																	<div className="flex flex-col">
																		<span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
																			{transaction.description}
																		</span>
																		<span className={`text-xs ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
																			{format(new Date(transaction.date), 'dd/MM/yyyy')} · {transaction.category}
																		</span>
																	</div>
																	<span className={`text-sm font-semibold ${
																		amount >= 0
																			? theme === 'dark' ? 'text-emerald-300' : 'text-emerald-700'
																			: theme === 'dark' ? 'text-rose-300' : 'text-rose-700'
																	}`}>
																		{formattedValue}
																	</span>
																</div>
															);
														})}
														{monthTransactions.length > 5 && (
															<p className={`px-4 py-2 text-xs italic ${
																theme === 'dark' ? 'text-slate-400' : 'text-slate-600'
															}`}>
																Exibindo as 5 primeiras movimentações.
															</p>
														)}
													</div>
												</div>
											</motion.div>
										)}
									</AnimatePresence>
								</div>
							);
						})}
					</div>
				</div>

				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
					<LineChart title="Evolução Anual (12 meses)" />
                                    <BarChart title="Receitas vs Saídas (trimestre)" />
				</div>

				<div className={`rounded-lg p-4 ${theme === 'dark' ? 'bg-slate-800' : 'bg-white border border-teal-200'}`}>
					<h3 className={`text-lg font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
						Filtros
					</h3>
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
						<div>
							<label className={`block text-sm mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
								Data Início
							</label>
							<input
								type="date"
								value={dateRange.start}
								onChange={(e) => setDateRange((prev) => ({ ...prev, start: e.target.value }))}
								className={`w-full border rounded-lg px-3 py-2 focus:outline-none transition-colors ${
									theme === 'dark'
										? 'bg-slate-700 border-slate-600 text-white focus:border-emerald-500'
										: 'bg-white border-gray-300 text-gray-900 focus:border-teal-500'
								}`}
							/>
						</div>
						<div>
							<label className={`block text-sm mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
								Data Fim
							</label>
							<input
								type="date"
								value={dateRange.end}
								onChange={(e) => setDateRange((prev) => ({ ...prev, end: e.target.value }))}
								className={`w-full border rounded-lg px-3 py-2 focus:outline-none transition-colors ${
									theme === 'dark'
										? 'bg-slate-700 border-slate-600 text-white focus:border-emerald-500'
										: 'bg-white border-gray-300 text-gray-900 focus:border-teal-500'
								}`}
							/>
						</div>
						<div>
							<label className={`block text-sm mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
								Categoria
							</label>
							<select
								value={selectedCategory}
								onChange={(e) => setSelectedCategory(e.target.value)}
								className={`w-full border rounded-lg px-3 py-2 focus:outline-none transition-colors ${
									theme === 'dark'
										? 'bg-slate-700 border-slate-600 text-white focus:border-emerald-500'
										: 'bg-white border-gray-300 text-gray-900 focus:border-teal-500'
								}`}
							>
								<option value="all">Todas</option>
								{categories.map((category) => (
									<option key={category} value={category}>
										{category}
									</option>
								))}
							</select>
						</div>
					</div>
				</div>

				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
					<div className={`rounded-lg p-4 ${theme === 'dark' ? 'bg-slate-800' : 'bg-white border border-teal-200'}`}>
						<div className="flex items-center gap-3 mb-2">
							<TrendingUp className={theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'} size={20} />
							<h3 className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
								Taxa de Poupança
							</h3>
						</div>
						<p className={`text-2xl font-bold ${theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'}`}>
							{savingsRate}%
						</p>
						<p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                                    Receitas - Saídas / Receitas
						</p>
					</div>

					<div className={`rounded-lg p-4 ${theme === 'dark' ? 'bg-slate-800' : 'bg-white border border-teal-200'}`}>
						<div className="flex items-center gap-3 mb-2">
							<Calculator className={theme === 'dark' ? 'text-violet-400' : 'text-violet-600'} size={20} />
							<h3 className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
								Receita Média
							</h3>
						</div>
						<p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
							{formatCurrency(averageIncome)}
						</p>
						<p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Por semana</p>
					</div>

					<div className={`rounded-lg p-4 ${theme === 'dark' ? 'bg-slate-800' : 'bg-white border border-teal-200'}`}>
						<div className="flex items-center gap-3 mb-2">
							<FileText className={theme === 'dark' ? 'text-blue-400' : 'text-blue-600'} size={20} />
							<h3 className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
								Transações
							</h3>
						</div>
						<p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
							{filteredTransactions.length}
						</p>
						<p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
							No período filtrado
						</p>
					</div>
				</div>

				<div className={`rounded-lg ${theme === 'dark' ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-teal-200'}`}>
					<button
						type="button"
						onClick={() => setIsDetailsExpanded((prev) => !prev)}
						className="w-full px-5 py-4 flex items-center justify-between gap-4"
					>
						<div>
							<h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
								Dados detalhados
							</h3>
							<p className={`text-xs mt-1 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
								💡 Clique para expandir e visualizar todas as transações do período
							</p>
						</div>
						<div className="flex items-center gap-3">
							<span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
								{filteredTransactions.length} registros
							</span>
							<ChevronRight
								size={18}
								className={`transition-transform ${isDetailsExpanded ? 'rotate-90' : ''} ${
									theme === 'dark' ? 'text-slate-300' : 'text-slate-500'
								}`}
							/>
						</div>
					</button>

					<AnimatePresence initial={false}>
						{isDetailsExpanded && (
							<motion.div
								initial={{ opacity: 0, height: 0 }}
								animate={{ opacity: 1, height: 'auto' }}
								exit={{ opacity: 0, height: 0 }}
								transition={{ duration: 0.25 }}
								className="px-5 pb-5"
							>
								<div className="overflow-x-auto">
									<table className="w-full">
										<thead>
											<tr className={`border-b ${theme === 'dark' ? 'border-slate-700' : 'border-gray-200'}`}>
												<th className={`text-left font-medium py-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
													Data
												</th>
												<th className={`text-left font-medium py-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
													Descrição
												</th>
												<th className={`text-left font-medium py-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
													Categoria
												</th>
												<th className={`text-right font-medium py-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
													Valor
												</th>
											</tr>
										</thead>
										<tbody>
											{filteredTransactions.map((item, index) => (
												<tr
													key={item.id ?? index}
													className={`border-b transition-colors ${
														theme === 'dark'
															? 'border-slate-700 hover:bg-slate-700/50'
															: 'border-gray-100 hover:bg-teal-50'
													}`}
												>
													<td className={`py-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
														{format(new Date(item.date), 'dd/MM/yyyy')}
													</td>
													<td className={`py-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
														{item.description}
													</td>
													<td className={`py-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
														{item.category}
													</td>
													<td className={`py-3 text-right font-semibold ${
														item.amount >= 0
															? theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'
															: theme === 'dark' ? 'text-red-400' : 'text-red-600'
													}`}>
														{item.amount >= 0 ? '+' : '-'}{formatCurrency(Math.abs(item.amount))}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</motion.div>
						)}
					</AnimatePresence>
				</div>
			</motion.div>
		);
};

export default Relatorios;
